/**
 * Feedback session toolbar — side-effect entry.
 *
 * Importing `commerce-kit/feedback-toolbar` (or `commerce-kit/browser` for the
 * combined entry) mounts a floating toolbar onto the page that lets reviewers
 * leave click-anchored comments. Comments persist via YNS API endpoints,
 * authenticated with the `better-auth` session cookie sent through
 * `credentials: "include"`.
 *
 * Gated on `process.env.NEXT_PUBLIC_VERCEL_ENV === "preview"` — only Vercel
 * preview deploys get the toolbar. Sandbox dev (env undefined) and production
 * (env === "production") skip it.
 */

import { type CSSProperties, type FormEvent, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

const MOUNT_NODE_ID = "yns-feedback-toolbar-root";

interface FeedbackAttachment {
	url: string;
	contentType: string;
	width?: number;
	height?: number;
}

interface FeedbackComment {
	id: string;
	pagePath: string;
	cssSelector: string;
	content: string;
	status: "todo" | "done";
	offsetXRatio: number;
	offsetYRatio: number;
	attachments: FeedbackAttachment[];
}

type SessionStatus = "created" | "in_progress" | "processing" | "in_review" | "done";

interface ReviewProgress {
	fillPct: number;
	label: string;
}

interface ActiveSession {
	feedbackSessionId: string;
	comments: FeedbackComment[];
	canComment: boolean;
	sessionStatus: SessionStatus;
	commentTotal: number;
	commentDone: number;
	eta: string;
	progress: ReviewProgress;
}

interface PendingPin {
	cssSelector: string;
	pagePath: string;
	surroundingHtml: string;
	rect: { top: number; left: number; width: number; height: number };
	clickX: number;
	clickY: number;
	offsetXRatio: number;
	offsetYRatio: number;
}

interface User {
	id: string;
	name: string | null;
	email: string;
	image: string | null;
}

interface AnonymousResponse {
	viewer: "anonymous";
	session: { id: string; status: SessionStatus };
	loginUrl: string;
}

interface NonMemberResponse {
	viewer: "non-member";
	session: { id: string; status: SessionStatus };
	user: User;
	accessDenied: true;
}

type MemberResponse = ActiveSession & { viewer: "member"; user: User };

type FeedbackResponse = AnonymousResponse | NonMemberResponse | MemberResponse;

const buildApiBase = (): string | null => {
	if (typeof window === "undefined") return null;
	const override = (process.env.NEXT_PUBLIC_YNS_API_BASE ?? "").trim();
	if (override) return override.replace(/\/$/, "");

	// Toolbar runs on a per-store preview deploy (mystore-preview.yns.{store|cx})
	// served by the merchant's Vercel project — that host has no YNS API. Aim
	// at the apex `yns.store` / `yns.cx`, which yns-app serves and where the
	// API lives. Cookies scoped to `.yns.store` / `.yns.cx` travel along.
	const host = window.location.hostname;
	const protocol = window.location.protocol;
	if (host.endsWith(".yns.store")) return `${protocol}//yns.store`;
	if (host.endsWith(".yns.cx")) return `${protocol}//yns.cx`;
	return window.location.origin;
};

const computeCssSelector = (el: Element): string => {
	if (el.id) return `#${CSS.escape(el.id)}`;
	const path: string[] = [];
	let node: Element | null = el;
	while (node && node.nodeType === Node.ELEMENT_NODE && path.length < 6) {
		let part = node.tagName.toLowerCase();
		const className = node.getAttribute("class");
		if (className) {
			const classes = className
				.split(/\s+/)
				.filter(Boolean)
				.slice(0, 2)
				.map((c) => `.${CSS.escape(c)}`)
				.join("");
			part += classes;
		}
		const parent: Element | null = node.parentElement;
		const tag = node.tagName;
		if (parent) {
			const siblings: Element[] = Array.from(parent.children).filter((c) => c.tagName === tag);
			if (siblings.length > 1) {
				const idx = siblings.indexOf(node) + 1;
				part += `:nth-of-type(${idx})`;
			}
		}
		path.unshift(part);
		node = parent;
	}
	return path.join(" > ");
};

const ATTRS_OF_INTEREST = ["id", "class", "data-testid", "aria-label", "role", "name", "href", "src"];

const formatAttrs = (el: Element): string => {
	const parts: string[] = [];
	for (const attr of ATTRS_OF_INTEREST) {
		const v = el.getAttribute(attr);
		if (!v) continue;
		const trimmed = v.length > 80 ? `${v.slice(0, 80)}…` : v;
		parts.push(` ${attr}="${trimmed.replace(/"/g, "&quot;")}"`);
	}
	return parts.join("");
};

const formatTextContent = (el: Element): string => {
	const text = (el.textContent ?? "").replace(/\s+/g, " ").trim();
	if (!text) return "";
	return text.length > 100 ? `${text.slice(0, 100)}…` : text;
};

const buildSurroundingHtml = (target: Element): string => {
	const ancestors: Element[] = [];
	let cur: Element | null = target;
	while (cur && cur !== document.documentElement && ancestors.length < 5) {
		const parent: Element | null = cur.parentElement;
		if (!parent) break;
		ancestors.unshift(parent);
		cur = parent;
	}

	const lines: string[] = [];
	let depth = 0;
	for (const ancestor of ancestors) {
		const indent = "  ".repeat(depth);
		lines.push(`${indent}<${ancestor.tagName.toLowerCase()}${formatAttrs(ancestor)}>`);
		depth++;
	}

	const targetIndent = "  ".repeat(depth);
	const targetTag = target.tagName.toLowerCase();
	const targetText = formatTextContent(target);
	lines.push(
		`${targetIndent}<${targetTag}${formatAttrs(target)}>${
			targetText ? `${targetText}</${targetTag}>` : ""
		}  ← TARGET`,
	);

	if (!targetText) {
		const childIndent = "  ".repeat(depth + 1);
		const children = Array.from(target.children).slice(0, 6);
		for (const child of children) {
			const childText = formatTextContent(child);
			lines.push(
				`${childIndent}<${child.tagName.toLowerCase()}${formatAttrs(child)}>${
					childText ? `${childText}</${child.tagName.toLowerCase()}>` : ""
				}`,
			);
		}
		if (target.children.length > 6) {
			lines.push(`${childIndent}… (${target.children.length - 6} more children)`);
		}
		lines.push(`${targetIndent}</${targetTag}>`);
	}

	for (let i = ancestors.length - 1; i >= 0; i--) {
		const indent = "  ".repeat(i);
		const ancestor = ancestors[i];
		if (!ancestor) continue;
		lines.push(`${indent}</${ancestor.tagName.toLowerCase()}>`);
	}

	return lines.join("\n");
};

const isInsideToolbar = (el: Element | null): boolean => {
	let node = el;
	while (node) {
		if (node instanceof HTMLElement && node.dataset.ynsFeedbackUi === "true") return true;
		node = node.parentElement;
	}
	return false;
};

const IGNORED_TAGS = new Set(["HTML", "HEAD", "SCRIPT", "STYLE", "NOSCRIPT"]);

const POLL_INTERVAL_MEMBER_MS = 10_000;
const POLL_INTERVAL_LOBBY_MS = 30_000;

const isFeedbackResponse = (data: unknown): data is FeedbackResponse =>
	typeof data === "object" &&
	data !== null &&
	"viewer" in data &&
	(data.viewer === "anonymous" || data.viewer === "non-member" || data.viewer === "member");

// Format a server-computed ETA as remaining + absolute label. The deadline
// math itself lives in yns-app's `lib/feedback-comments-api.ts` so the toolbar
// and YNS lock screen stay in sync.
const formatEta = (etaIso: string): string => {
	const eta = new Date(etaIso);
	const remainingMs = eta.getTime() - Date.now();
	const dateLabel = eta.toLocaleString(undefined, {
		weekday: "short",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});

	if (remainingMs <= 0) return `Any moment now (estimated by ${dateLabel})`;
	const remainingMin = Math.ceil(remainingMs / 60_000);
	if (remainingMin < 60) return `~${remainingMin} minutes (by ${dateLabel})`;
	const remainingHours = Math.round(remainingMs / 3_600_000);
	if (remainingHours < 24) {
		return `~${remainingHours} ${remainingHours === 1 ? "hour" : "hours"} (by ${dateLabel})`;
	}
	const remainingDays = Math.round(remainingMs / 86_400_000);
	return `~${remainingDays} ${remainingDays === 1 ? "day" : "days"} (by ${dateLabel})`;
};

// Sign-out hits the YNS API origin — the same cross-origin host the poll
// targets. Auth cookies live on the YNS host; the preview host has no auth
// routes. `credentials: "include"` + CORS carries the YNS cookie both ways.
async function signOutAndPoll(apiBase: string, pollNow: () => void) {
	try {
		const res = await fetch(`${apiBase}/api/auth/sign-out`, {
			method: "POST",
			credentials: "include",
		});
		if (!res.ok) {
			console.warn("[YNS Feedback Toolbar] sign-out responded non-OK", res.status);
		}
	} catch (err) {
		// Network failures don't block the re-poll; the next tick reflects truth.
		console.warn("[YNS Feedback Toolbar] sign-out fetch failed", err);
	}
	pollNow();
}

function FeedbackToolbar() {
	const [response, setResponse] = useState<FeedbackResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [pinMode, setPinMode] = useState(false);
	const [pending, setPending] = useState<PendingPin | null>(null);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [finalizing, setFinalizing] = useState(false);
	const apiBase = useRef<string | null>(null);
	const pollNowRef = useRef<() => void>(() => {});

	useEffect(() => {
		apiBase.current = buildApiBase();
		if (!apiBase.current) {
			setLoading(false);
			return;
		}

		let cancelled = false;
		// Latest controller across in-flight fetch + the apply-id that gates which
		// response is allowed to commit. Both refs together fence the polling loop
		// against user actions (sign-out, finalize) and visibility transitions.
		let abortController: AbortController | null = null;
		let latestApplyId = 0;
		let pollTimeout: number | null = null;
		// Tracks the latest applied viewer so cadence can be read synchronously
		// at scheduling time without waiting for setResponse to flush.
		let latestViewer: FeedbackResponse["viewer"] | null = null;

		const clearTimer = () => {
			if (pollTimeout !== null) {
				window.clearTimeout(pollTimeout);
				pollTimeout = null;
			}
		};

		const applyResponse = (next: FeedbackResponse | null, myId: number) => {
			if (cancelled || myId < latestApplyId) return;
			latestViewer = next?.viewer ?? null;
			setResponse(next);
			setLoading(false);
		};

		const scheduleNext = () => {
			if (cancelled) return;
			const cadence = latestViewer === "member" ? POLL_INTERVAL_MEMBER_MS : POLL_INTERVAL_LOBBY_MS;
			pollTimeout = window.setTimeout(() => {
				void fetchOnce();
			}, cadence);
		};

		const fetchOnce = async () => {
			if (cancelled) return;
			abortController?.abort();
			const controller = new AbortController();
			abortController = controller;
			const myId = ++latestApplyId;
			try {
				const res = await fetch(
					`${apiBase.current}/api/feedback-comments?host=${encodeURIComponent(window.location.host)}`,
					{ credentials: "include", signal: controller.signal },
				);
				if (cancelled || myId < latestApplyId) return;
				if (res.status === 404 || !res.ok) {
					applyResponse(null, myId);
					scheduleNext();
					return;
				}
				const data = (await res.json()) as unknown;
				if (cancelled || myId < latestApplyId) return;
				if (!isFeedbackResponse(data)) {
					applyResponse(null, myId);
					scheduleNext();
					return;
				}
				applyResponse(data, myId);
				scheduleNext();
			} catch (err) {
				// AbortError fires whenever pollNow/visibilitychange cancels an
				// in-flight request — that's not a polling failure, just a normal
				// preemption. Don't clobber state or reschedule (the preempting
				// call is responsible for the next tick).
				if ((err as { name?: string } | null)?.name === "AbortError") return;
				if (cancelled) return;
				applyResponse(null, myId);
				scheduleNext();
			}
		};

		const pollNow = () => {
			if (cancelled) return;
			clearTimer();
			void fetchOnce();
		};
		pollNowRef.current = pollNow;

		const onVisibility = () => {
			if (document.hidden) {
				abortController?.abort();
				clearTimer();
			} else {
				pollNow();
			}
		};

		document.addEventListener("visibilitychange", onVisibility);
		void fetchOnce();

		return () => {
			cancelled = true;
			document.removeEventListener("visibilitychange", onVisibility);
			abortController?.abort();
			clearTimer();
			pollNowRef.current = () => {};
		};
	}, []);

	// Derived alias so the existing member-branch code (which reads `session.foo`)
	// keeps working without rewrites. Anonymous/non-member render paths return
	// before this is consulted.
	const session: MemberResponse | null = response?.viewer === "member" ? response : null;

	// When the session stops accepting comments (finalized → in_review, or
	// closed), tear down any in-flight UI so the hover overlay/popover/sidebar
	// don't linger after the toolbar's main render returns null.
	useEffect(() => {
		if (!session || session.canComment) return;
		setPinMode(false);
		setPending(null);
		setSidebarOpen(false);
		setEditingId(null);
	}, [session]);

	useEffect(() => {
		if (!pinMode) return;
		document.body.style.cursor = "crosshair";
		return () => {
			document.body.style.cursor = "";
		};
	}, [pinMode]);

	useEffect(() => {
		if (!pinMode) return;

		const overlay = document.createElement("div");
		overlay.dataset.ynsFeedbackUi = "true";
		overlay.style.cssText = [
			"position: fixed",
			"pointer-events: none",
			"z-index: 2147483644",
			"border: 2px dashed #10b981",
			"background: rgba(16, 185, 129, 0.08)",
			"border-radius: 3px",
			"display: none",
			"transition: top 0.05s, left 0.05s, width 0.05s, height 0.05s",
		].join(";");

		const label = document.createElement("div");
		label.dataset.ynsFeedbackUi = "true";
		label.textContent = "Click to comment";
		label.style.cssText = [
			"position: fixed",
			"pointer-events: none",
			"z-index: 2147483645",
			"background: #059669",
			"color: #fff",
			"font-size: 11px",
			"font-family: ui-sans-serif, system-ui, sans-serif",
			"padding: 3px 8px",
			"border-radius: 4px",
			"white-space: nowrap",
			"display: none",
			"box-shadow: 0 2px 6px rgba(0,0,0,0.15)",
		].join(";");

		document.documentElement.appendChild(overlay);
		document.documentElement.appendChild(label);

		let hovered: Element | null = null;
		const handleMove = (e: MouseEvent) => {
			const el = document.elementFromPoint(e.clientX, e.clientY);
			if (!el || IGNORED_TAGS.has(el.tagName) || isInsideToolbar(el)) {
				overlay.style.display = "none";
				label.style.display = "none";
				hovered = null;
				return;
			}
			hovered = el;
			requestAnimationFrame(() => {
				if (hovered !== el) return;
				const rect = el.getBoundingClientRect();
				overlay.style.top = `${rect.top}px`;
				overlay.style.left = `${rect.left}px`;
				overlay.style.width = `${rect.width}px`;
				overlay.style.height = `${rect.height}px`;
				overlay.style.display = "block";
				label.style.left = `${e.clientX + 14}px`;
				label.style.top = `${e.clientY + 14}px`;
				label.style.display = "block";
			});
		};

		const handleLeave = () => {
			overlay.style.display = "none";
			label.style.display = "none";
			hovered = null;
		};

		document.addEventListener("mousemove", handleMove, true);
		document.addEventListener("mouseleave", handleLeave);

		return () => {
			document.removeEventListener("mousemove", handleMove, true);
			document.removeEventListener("mouseleave", handleLeave);
			overlay.remove();
			label.remove();
		};
	}, [pinMode]);

	useEffect(() => {
		if (!pinMode) return;
		const handleClick = (event: MouseEvent) => {
			const target = event.target;
			if (!(target instanceof Element)) return;
			if (isInsideToolbar(target)) return;

			event.preventDefault();
			event.stopPropagation();

			const rect = target.getBoundingClientRect();
			const offsetXRatio = rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0.5;
			const offsetYRatio = rect.height > 0 ? (event.clientY - rect.top) / rect.height : 0.5;
			setPending({
				cssSelector: computeCssSelector(target),
				pagePath: window.location.pathname,
				surroundingHtml: buildSurroundingHtml(target),
				rect: {
					top: rect.top + window.scrollY,
					left: rect.left + window.scrollX,
					width: rect.width,
					height: rect.height,
				},
				clickX: event.clientX + window.scrollX,
				clickY: event.clientY + window.scrollY,
				offsetXRatio: Math.min(1, Math.max(0, offsetXRatio)),
				offsetYRatio: Math.min(1, Math.max(0, offsetYRatio)),
			});
			setPinMode(false);
		};
		document.addEventListener("click", handleClick, { capture: true });
		return () => document.removeEventListener("click", handleClick, { capture: true });
	}, [pinMode]);

	// After a mutation (POST/PATCH/DELETE), route through the polling loop so the
	// discriminated-union handler validates the response and the apply-id guard
	// still fences against races. A non-member/anonymous shape coerced into
	// ActiveSession here would corrupt state otherwise.
	const refreshComments = () => {
		pollNowRef.current();
	};

	const submitNewComment = async (content: string, attachments: FeedbackAttachment[]) => {
		if (!apiBase.current || !session || !pending) return;
		const res = await fetch(`${apiBase.current}/api/feedback-comments`, {
			method: "POST",
			credentials: "include",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				feedbackSessionId: session.feedbackSessionId,
				content,
				pagePath: pending.pagePath,
				cssSelector: pending.cssSelector,
				surroundingHtml: pending.surroundingHtml,
				offsetXRatio: pending.offsetXRatio,
				offsetYRatio: pending.offsetYRatio,
				...(attachments.length > 0 ? { attachments } : {}),
			}),
		});
		if (!res.ok) return;
		setPending(null);
		setPinMode(true);
		refreshComments();
	};

	const updateComment = async (id: string, content: string, attachments: FeedbackAttachment[]) => {
		if (!apiBase.current) return;
		const res = await fetch(`${apiBase.current}/api/feedback-comments/${id}`, {
			method: "PATCH",
			credentials: "include",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ content, attachments }),
		});
		if (!res.ok) return;
		setEditingId(null);
		refreshComments();
	};

	const removeComment = async (id: string) => {
		if (!apiBase.current) return;
		const res = await fetch(`${apiBase.current}/api/feedback-comments/${id}`, {
			method: "DELETE",
			credentials: "include",
		});
		if (!res.ok) return;
		refreshComments();
	};

	const finalizeSession = async () => {
		if (!apiBase.current || !session) return;
		if (!window.confirm("Finalize this feedback session? You won't be able to add more comments.")) return;
		setFinalizing(true);
		try {
			const res = await fetch(
				`${apiBase.current}/api/feedback-sessions/${session.feedbackSessionId}/finalize`,
				{ method: "POST", credentials: "include" },
			);
			if (!res.ok) return;
			// Flip BOTH canComment and sessionStatus locally. Without the status
			// flip, the render guard sees `canComment=false` + `status=in_progress`
			// (a state the SubmittedPanel branch returns null for) and the toolbar
			// disappears for the ~10s gap until the next poll catches up.
			const body = (await res.json().catch(() => null)) as { status?: SessionStatus } | null;
			const nextStatus: SessionStatus = body?.status ?? "processing";
			setResponse((prev) =>
				prev?.viewer === "member" ? { ...prev, canComment: false, sessionStatus: nextStatus } : prev,
			);
		} finally {
			setFinalizing(false);
		}
	};

	const scrollToComment = (comment: FeedbackComment) => {
		if (comment.pagePath !== window.location.pathname) {
			window.location.assign(comment.pagePath);
			return;
		}
		let el: Element | null = null;
		try {
			el = document.querySelector(comment.cssSelector);
		} catch {
			el = null;
		}
		if (!el) return;
		el.scrollIntoView({ behavior: "smooth", block: "center" });
		setEditingId(comment.id);
	};

	if (loading || !response) return null;

	if (response.viewer === "anonymous") {
		return <AnonymousPill loginUrl={response.loginUrl} />;
	}

	if (response.viewer === "non-member") {
		return (
			<NonMemberPill
				user={response.user}
				onSignOut={() => {
					if (!apiBase.current) return;
					void signOutAndPoll(apiBase.current, () => pollNowRef.current());
				}}
			/>
		);
	}

	// response.viewer === "member" — `session` (derived above) mirrors `response`.
	// The redundant null guard satisfies TypeScript since the derivation can't
	// narrow across the early returns above.
	if (!session) return null;

	// Session has been finalized for AI review. Show a small status panel with
	// the same progress + ETA shown in YNS, but no commenting controls.
	if (!session.canComment) {
		if (session.sessionStatus !== "processing" && session.sessionStatus !== "in_review") return null;
		return (
			<div data-yns-feedback-ui="true">
				<SubmittedPanel progress={session.progress} eta={session.eta} status={session.sessionStatus} />
			</div>
		);
	}

	const visiblePins = session.comments.filter(
		(c) => c.pagePath === window.location.pathname && c.status !== "done",
	);

	return (
		<div data-yns-feedback-ui="true">
			{visiblePins.map((pin, idx) => (
				<PinOverlay
					key={pin.id}
					pin={pin}
					number={idx + 1}
					editing={editingId === pin.id}
					feedbackSessionId={session.feedbackSessionId}
					apiBase={apiBase.current}
					onStartEdit={() => setEditingId(pin.id)}
					onCancelEdit={() => setEditingId(null)}
					onSave={(content, attachments) => updateComment(pin.id, content, attachments)}
					onRemove={() => removeComment(pin.id)}
				/>
			))}

			{pending && (
				<PendingCommentPopover
					pending={pending}
					feedbackSessionId={session.feedbackSessionId}
					apiBase={apiBase.current}
					onCancel={() => {
						setPending(null);
						setPinMode(true);
					}}
					onSave={(content, attachments) => submitNewComment(content, attachments)}
				/>
			)}

			{sidebarOpen && (
				<CommentsSidebar
					comments={session.comments}
					currentPath={window.location.pathname}
					onClose={() => setSidebarOpen(false)}
					onSelect={scrollToComment}
				/>
			)}

			<div style={toolbarStyle}>
				<button
					type="button"
					onClick={() => setPinMode((v) => !v)}
					style={pinMode ? toolbarButtonActiveStyle : toolbarButtonStyle}
				>
					{pinMode ? "Cancel" : "Add comment"}
				</button>
				<button type="button" onClick={() => setSidebarOpen((v) => !v)} style={toolbarButtonGhostStyle}>
					{sidebarOpen ? "Hide list" : `List (${session.comments.length})`}
				</button>
				<button
					type="button"
					onClick={finalizeSession}
					disabled={finalizing}
					style={toolbarButtonFinalizeStyle}
				>
					{finalizing ? "Finalizing…" : "Finalize"}
				</button>
				<span style={toolbarHintStyle}>
					{pinMode ? "Click any element to comment" : `${visiblePins.length} on this page`}
				</span>
				<IdentityChip
					user={session.user}
					onSignOut={() => {
						if (!apiBase.current) return;
						void signOutAndPoll(apiBase.current, () => pollNowRef.current());
					}}
				/>
			</div>
		</div>
	);
}

function SubmittedPanel({
	progress,
	eta: etaIso,
	status,
}: {
	progress: ReviewProgress;
	eta: string;
	status: SessionStatus;
}) {
	// Re-render every minute so the relative ETA label stays fresh between
	// the 10s data polls (which only re-render when the API payload changes).
	const [, setTick] = useState(0);
	useEffect(() => {
		const id = window.setInterval(() => setTick((t) => t + 1), 60_000);
		return () => window.clearInterval(id);
	}, []);

	const eta = formatEta(etaIso);
	const isInReview = status === "in_review";
	const headline = isInReview ? "Feedback under review" : "Applying feedback";

	return (
		<div style={submittedPanelStyle}>
			<style>{spinnerKeyframes}</style>
			<div style={submittedHeaderStyle}>
				<Spinner />
				<span style={submittedBadgeStyle}>Submitted</span>
				<strong style={{ fontSize: 13 }}>{headline}</strong>
			</div>
			<div style={submittedProgressLabelStyle}>
				<span>Review progress</span>
				<span style={{ opacity: 0.7 }}>{progress.label}</span>
			</div>
			<div style={submittedProgressTrackStyle}>
				<div style={{ ...submittedProgressFillStyle, width: `${progress.fillPct}%` }} />
			</div>
			<p style={submittedEtaStyle}>
				Estimated delivery: <span style={{ fontWeight: 600 }}>{eta}</span>
			</p>
		</div>
	);
}

// Inline keyframes — toolbar avoids relying on a global stylesheet.
const spinnerKeyframes = `@keyframes yns-feedback-spin { to { transform: rotate(360deg); } }`;

function Spinner({ size = 14 }: { size?: number }) {
	return (
		<span
			aria-hidden
			style={{
				display: "inline-block",
				width: size,
				height: size,
				border: "2px solid rgba(16, 185, 129, 0.25)",
				borderTopColor: "#10b981",
				borderRadius: 999,
				animation: "yns-feedback-spin 0.9s linear infinite",
			}}
		/>
	);
}

function PinOverlay({
	pin,
	number,
	editing,
	feedbackSessionId,
	apiBase,
	onStartEdit,
	onCancelEdit,
	onSave,
	onRemove,
}: {
	pin: FeedbackComment;
	number: number;
	editing: boolean;
	feedbackSessionId: string;
	apiBase: string | null;
	onStartEdit: () => void;
	onCancelEdit: () => void;
	onSave: (content: string, attachments: FeedbackAttachment[]) => Promise<void>;
	onRemove: () => Promise<void>;
}) {
	const target = useTargetPosition(pin.cssSelector, pin.offsetXRatio, pin.offsetYRatio);
	if (!target) return null;

	return (
		<div
			style={{
				position: "absolute",
				top: target.top,
				left: target.left,
				zIndex: 2147483600,
				pointerEvents: "auto",
			}}
		>
			<button type="button" onClick={onStartEdit} style={pinDotStyle} title={pin.content}>
				{number}
			</button>
			{editing && (
				<EditPopover
					initial={pin.content}
					initialAttachments={pin.attachments}
					feedbackSessionId={feedbackSessionId}
					apiBase={apiBase}
					onCancel={onCancelEdit}
					onSave={onSave}
					onRemove={onRemove}
				/>
			)}
		</div>
	);
}

function PendingCommentPopover({
	pending,
	feedbackSessionId,
	apiBase,
	onCancel,
	onSave,
}: {
	pending: PendingPin;
	feedbackSessionId: string;
	apiBase: string | null;
	onCancel: () => void;
	onSave: (content: string, attachments: FeedbackAttachment[]) => Promise<void>;
}) {
	return (
		<>
			<div
				style={{
					position: "absolute",
					top: pending.rect.top + pending.rect.height * pending.offsetYRatio - 12,
					left: pending.rect.left + pending.rect.width * pending.offsetXRatio - 12,
					zIndex: 2147483600,
					pointerEvents: "none",
				}}
			>
				<div style={pinDotStyle}>•</div>
			</div>
			<div
				style={{
					position: "absolute",
					top: pending.clickY + 10,
					left: pending.clickX + 10,
					zIndex: 2147483647,
				}}
			>
				<EditPopover
					initial=""
					initialAttachments={[]}
					feedbackSessionId={feedbackSessionId}
					apiBase={apiBase}
					onCancel={onCancel}
					onSave={onSave}
				/>
			</div>
		</>
	);
}

function CommentsSidebar({
	comments,
	currentPath,
	onClose,
	onSelect,
}: {
	comments: FeedbackComment[];
	currentPath: string;
	onClose: () => void;
	onSelect: (comment: FeedbackComment) => void;
}) {
	const groups = new Map<string, FeedbackComment[]>();
	for (const c of comments) {
		const list = groups.get(c.pagePath) ?? [];
		list.push(c);
		groups.set(c.pagePath, list);
	}
	const paths = Array.from(groups.keys()).sort((a, b) => {
		if (a === currentPath) return -1;
		if (b === currentPath) return 1;
		return a.localeCompare(b);
	});

	return (
		<div style={sidebarStyle}>
			<div style={sidebarHeaderStyle}>
				<strong style={{ fontSize: 14 }}>Comments ({comments.length})</strong>
				<button type="button" onClick={onClose} style={ghostButtonStyle}>
					Close
				</button>
			</div>
			<div style={sidebarScrollStyle}>
				{comments.length === 0 ? (
					<div style={sidebarEmptyStyle}>No comments yet. Click “Add comment” to leave one.</div>
				) : (
					paths.map((path) => (
						<div key={path} style={{ marginBottom: 12 }}>
							<div style={sidebarPathStyle}>{path === currentPath ? `${path} · current` : path}</div>
							{(groups.get(path) ?? []).map((c, idx) => (
								<button
									key={c.id}
									type="button"
									onClick={() => onSelect(c)}
									style={sidebarItemStyle}
									disabled={c.status === "done" && false}
								>
									<span style={sidebarItemIndexStyle}>{idx + 1}</span>
									<span style={sidebarItemTextStyle}>
										{c.content.length > 140 ? `${c.content.slice(0, 140)}…` : c.content}
									</span>
									{c.status === "done" && <span style={sidebarItemDoneStyle}>done</span>}
								</button>
							))}
						</div>
					))
				)}
			</div>
		</div>
	);
}

const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

const readImageDimensions = (file: File): Promise<{ width: number; height: number } | null> =>
	new Promise((resolve) => {
		const url = URL.createObjectURL(file);
		const img = new window.Image();
		img.onload = () => {
			URL.revokeObjectURL(url);
			resolve({ width: img.naturalWidth, height: img.naturalHeight });
		};
		img.onerror = () => {
			URL.revokeObjectURL(url);
			resolve(null);
		};
		img.src = url;
	});

function EditPopover({
	initial,
	initialAttachments,
	feedbackSessionId,
	apiBase,
	onCancel,
	onSave,
	onRemove,
}: {
	initial: string;
	initialAttachments: FeedbackAttachment[];
	feedbackSessionId: string;
	apiBase: string | null;
	onCancel: () => void;
	onSave: (content: string, attachments: FeedbackAttachment[]) => Promise<void>;
	onRemove?: () => Promise<void>;
}) {
	const [value, setValue] = useState(initial);
	const [attachments, setAttachments] = useState<FeedbackAttachment[]>(initialAttachments);
	const [saving, setSaving] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const textareaRef = useRef<HTMLTextAreaElement | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		const el = textareaRef.current;
		if (!el) return;
		el.focus();
		const len = el.value.length;
		el.setSelectionRange(len, len);
	}, []);

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		const content = value.trim();
		if (!content) return;
		setSaving(true);
		try {
			await onSave(content, attachments);
		} finally {
			setSaving(false);
		}
	};

	const handleFiles = async (files: FileList | null) => {
		if (!files || files.length === 0 || !apiBase) return;
		setError(null);
		const slots = MAX_ATTACHMENTS - attachments.length;
		if (slots <= 0) {
			setError(`Max ${MAX_ATTACHMENTS} images per comment`);
			return;
		}
		const picked = Array.from(files).slice(0, slots);
		for (const f of picked) {
			if (!f.type.startsWith("image/")) {
				setError(`"${f.name}" is not an image`);
				return;
			}
			if (f.size > MAX_ATTACHMENT_BYTES) {
				setError(`"${f.name}" exceeds 5 MB`);
				return;
			}
		}

		setUploading(true);
		try {
			const dims = await Promise.all(picked.map(readImageDimensions));
			const fd = new FormData();
			picked.forEach((file, i) => {
				fd.append("file", file);
				fd.append("width", dims[i]?.width ? String(dims[i]?.width) : "");
				fd.append("height", dims[i]?.height ? String(dims[i]?.height) : "");
			});
			const res = await fetch(
				`${apiBase}/api/feedback-comments/uploads?feedbackSessionId=${encodeURIComponent(feedbackSessionId)}`,
				{ method: "POST", credentials: "include", body: fd },
			);
			if (!res.ok) {
				const body = (await res.json().catch(() => null)) as { error?: string } | null;
				setError(body?.error ?? "Upload failed");
				return;
			}
			const body = (await res.json()) as { uploads: FeedbackAttachment[] };
			setAttachments((prev) => [...prev, ...body.uploads]);
		} catch {
			setError("Upload failed");
		} finally {
			setUploading(false);
			if (fileInputRef.current) fileInputRef.current.value = "";
		}
	};

	const removeAttachment = (url: string) => {
		setAttachments((prev) => prev.filter((a) => a.url !== url));
	};

	return (
		<form onSubmit={handleSubmit} style={popoverStyle}>
			<textarea
				ref={textareaRef}
				value={value}
				onChange={(e) => setValue(e.target.value)}
				placeholder="Leave a comment…"
				style={textareaStyle}
				rows={3}
			/>
			{attachments.length > 0 && (
				<div style={attachmentRowStyle}>
					{attachments.map((att) => (
						<div key={att.url} style={attachmentThumbWrapStyle}>
							<img src={att.url} alt="" style={attachmentThumbStyle} />
							<button
								type="button"
								onClick={() => removeAttachment(att.url)}
								style={attachmentRemoveStyle}
								disabled={saving || uploading}
								aria-label="Remove attachment"
							>
								×
							</button>
						</div>
					))}
				</div>
			)}
			{error && <div style={errorTextStyle}>{error}</div>}
			<input
				ref={fileInputRef}
				type="file"
				accept="image/*"
				multiple
				onChange={(e) => void handleFiles(e.target.files)}
				style={{ display: "none" }}
			/>
			<div style={popoverActionsStyle}>
				{onRemove && (
					<button type="button" onClick={() => onRemove()} style={dangerButtonStyle} disabled={saving}>
						Delete
					</button>
				)}
				<button
					type="button"
					onClick={() => fileInputRef.current?.click()}
					style={attachIconButtonStyle}
					disabled={saving || uploading || attachments.length >= MAX_ATTACHMENTS}
					aria-label={uploading ? "Uploading…" : "Attach image"}
					title={uploading ? "Uploading…" : "Attach image"}
				>
					{uploading ? <SpinnerGlyph /> : <PaperclipGlyph />}
				</button>
				<div style={{ flex: 1 }} />
				<button type="button" onClick={onCancel} style={ghostButtonStyle} disabled={saving}>
					Cancel
				</button>
				<button type="submit" style={primaryButtonStyle} disabled={saving || uploading || !value.trim()}>
					{saving ? "Saving…" : "Save"}
				</button>
			</div>
		</form>
	);
}

function useTargetPosition(selector: string, offsetXRatio: number, offsetYRatio: number) {
	const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

	useEffect(() => {
		const update = () => {
			let el: Element | null = null;
			try {
				el = document.querySelector(selector);
			} catch {
				el = null;
			}
			if (!el) {
				setPos(null);
				return;
			}
			const r = el.getBoundingClientRect();
			setPos({
				top: r.top + window.scrollY + r.height * offsetYRatio - 12,
				left: r.left + window.scrollX + r.width * offsetXRatio - 12,
			});
		};

		update();
		const ro = new ResizeObserver(update);
		try {
			const el = document.querySelector(selector);
			if (el) ro.observe(el);
		} catch {
			// swallow invalid selector
		}
		window.addEventListener("scroll", update, true);
		window.addEventListener("resize", update);
		return () => {
			ro.disconnect();
			window.removeEventListener("scroll", update, true);
			window.removeEventListener("resize", update);
		};
	}, [selector, offsetXRatio, offsetYRatio]);

	return pos;
}

// Inline styles — toolbar is injected into arbitrary stores, so we avoid relying
// on any CSS framework being present.
const toolbarStyle: CSSProperties = {
	position: "fixed",
	bottom: 16,
	left: "50%",
	transform: "translateX(-50%)",
	zIndex: 2147483646,
	display: "flex",
	alignItems: "center",
	gap: 8,
	padding: "8px 12px",
	background: "rgba(17, 17, 17, 0.92)",
	color: "white",
	borderRadius: 999,
	boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
	fontFamily:
		'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
	fontSize: 14,
	pointerEvents: "auto",
};

const toolbarButtonStyle: CSSProperties = {
	border: "none",
	background: "white",
	color: "#111",
	padding: "6px 12px",
	borderRadius: 999,
	cursor: "pointer",
	fontWeight: 600,
	fontSize: 13,
};

const toolbarButtonActiveStyle: CSSProperties = {
	...toolbarButtonStyle,
	background: "#ef4444",
	color: "white",
};

const toolbarButtonGhostStyle: CSSProperties = {
	border: "1px solid rgba(255,255,255,0.4)",
	background: "transparent",
	color: "white",
	padding: "6px 12px",
	borderRadius: 999,
	cursor: "pointer",
	fontWeight: 500,
	fontSize: 13,
};

const toolbarButtonFinalizeStyle: CSSProperties = {
	border: "none",
	background: "#10b981",
	color: "white",
	padding: "6px 12px",
	borderRadius: 999,
	cursor: "pointer",
	fontWeight: 600,
	fontSize: 13,
};

const toolbarHintStyle: CSSProperties = {
	opacity: 0.8,
	fontSize: 12,
};

const pinDotStyle: CSSProperties = {
	width: 24,
	height: 24,
	borderRadius: 999,
	background: "#10b981",
	color: "white",
	border: "2px solid white",
	cursor: "pointer",
	fontSize: 12,
	fontWeight: 700,
	boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	padding: 0,
};

const popoverStyle: CSSProperties = {
	background: "white",
	border: "1px solid #e5e7eb",
	borderRadius: 8,
	padding: 12,
	width: 280,
	boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
	fontFamily:
		'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
	display: "flex",
	flexDirection: "column",
	gap: 8,
	color: "#111",
};

const textareaStyle: CSSProperties = {
	width: "100%",
	border: "1px solid #d1d5db",
	borderRadius: 6,
	padding: 8,
	fontSize: 14,
	resize: "vertical",
	fontFamily: "inherit",
	color: "#111",
	background: "white",
	boxSizing: "border-box",
};

const popoverActionsStyle: CSSProperties = {
	display: "flex",
	alignItems: "center",
	gap: 6,
};

const baseButton: CSSProperties = {
	border: "1px solid transparent",
	borderRadius: 6,
	padding: "6px 10px",
	fontSize: 13,
	cursor: "pointer",
	fontWeight: 500,
};

const primaryButtonStyle: CSSProperties = {
	...baseButton,
	background: "#111",
	color: "white",
};

const ghostButtonStyle: CSSProperties = {
	...baseButton,
	background: "transparent",
	color: "#374151",
	borderColor: "#d1d5db",
};

const dangerButtonStyle: CSSProperties = {
	...baseButton,
	background: "transparent",
	color: "#b91c1c",
	borderColor: "#fecaca",
};

const attachmentRowStyle: CSSProperties = {
	display: "flex",
	flexWrap: "wrap",
	gap: 6,
};

const attachmentThumbWrapStyle: CSSProperties = {
	position: "relative",
	width: 56,
	height: 56,
	borderRadius: 6,
	overflow: "hidden",
	border: "1px solid #e5e7eb",
	background: "#f9fafb",
};

const attachmentThumbStyle: CSSProperties = {
	width: "100%",
	height: "100%",
	objectFit: "cover",
	display: "block",
};

const attachmentRemoveStyle: CSSProperties = {
	position: "absolute",
	top: 2,
	right: 2,
	width: 18,
	height: 18,
	borderRadius: 999,
	border: "none",
	background: "rgba(17, 17, 17, 0.85)",
	color: "white",
	fontSize: 13,
	lineHeight: "16px",
	cursor: "pointer",
	padding: 0,
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
};

const errorTextStyle: CSSProperties = {
	color: "#b91c1c",
	fontSize: 12,
	margin: 0,
};

const attachIconButtonStyle: CSSProperties = {
	...baseButton,
	background: "transparent",
	color: "#374151",
	borderColor: "#d1d5db",
	width: 30,
	height: 30,
	padding: 0,
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	flexShrink: 0,
};

function PaperclipGlyph() {
	return (
		<svg
			role="img"
			aria-label="Attach image"
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<title>Attach image</title>
			<path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 17.93 8.83l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
		</svg>
	);
}

function SpinnerGlyph() {
	return (
		<>
			<style>{`@keyframes yns-attach-spin { to { transform: rotate(360deg); } }`}</style>
			<span
				aria-hidden
				style={{
					display: "inline-block",
					width: 12,
					height: 12,
					border: "2px solid rgba(55, 65, 81, 0.25)",
					borderTopColor: "#374151",
					borderRadius: 999,
					animation: "yns-attach-spin 0.9s linear infinite",
				}}
			/>
		</>
	);
}

const NARROW_BREAKPOINT_PX = 480;

function useIsNarrow(): boolean {
	const [narrow, setNarrow] = useState(false);
	useEffect(() => {
		const mq = window.matchMedia(`(max-width: ${NARROW_BREAKPOINT_PX}px)`);
		const update = () => setNarrow(mq.matches);
		update();
		mq.addEventListener("change", update);
		return () => mq.removeEventListener("change", update);
	}, []);
	return narrow;
}

const getInitials = (user: User): string => {
	const name = user.name?.trim();
	if (name) {
		const parts = name.split(/\s+/).filter(Boolean).slice(0, 2);
		return parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
	}
	return user.email[0]?.toUpperCase() ?? "?";
};

function Avatar({ user, size = 22 }: { user: User; size?: number }) {
	if (user.image) {
		return (
			<img
				src={user.image}
				alt=""
				width={size}
				height={size}
				style={{
					width: size,
					height: size,
					borderRadius: 999,
					objectFit: "cover",
					display: "block",
					flexShrink: 0,
				}}
			/>
		);
	}
	return (
		<span
			aria-hidden
			style={{
				width: size,
				height: size,
				borderRadius: 999,
				background: "rgba(255,255,255,0.18)",
				color: "white",
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				fontSize: Math.max(10, Math.round(size * 0.45)),
				fontWeight: 600,
				flexShrink: 0,
			}}
		>
			{getInitials(user)}
		</span>
	);
}

function AnonymousPill({ loginUrl }: { loginUrl: string }) {
	return (
		<div data-yns-feedback-ui="true">
			<div style={toolbarStyle}>
				<span style={toolbarHintStyle}>Log in to provide feedback</span>
				<button type="button" onClick={() => window.location.assign(loginUrl)} style={toolbarButtonStyle}>
					Log in
				</button>
			</div>
		</div>
	);
}

function NonMemberPill({ user, onSignOut }: { user: User; onSignOut: () => void }) {
	const narrow = useIsNarrow();
	return (
		<div data-yns-feedback-ui="true">
			<div style={toolbarStyle}>
				<Avatar user={user} />
				{!narrow && <span style={identityTextStyle}>{user.email}</span>}
				<span style={toolbarHintStyle}>You don't have access to this store</span>
				<button type="button" onClick={onSignOut} style={toolbarButtonStyle}>
					Sign out
				</button>
			</div>
		</div>
	);
}

function IdentityChip({ user, onSignOut }: { user: User; onSignOut: () => void }) {
	const [open, setOpen] = useState(false);
	const wrapRef = useRef<HTMLDivElement | null>(null);
	const narrow = useIsNarrow();

	useEffect(() => {
		if (!open) return;
		const onDown = (e: MouseEvent) => {
			if (!wrapRef.current) return;
			if (e.target instanceof Node && wrapRef.current.contains(e.target)) return;
			setOpen(false);
		};
		document.addEventListener("mousedown", onDown);
		return () => document.removeEventListener("mousedown", onDown);
	}, [open]);

	const displayName = user.name?.trim() || user.email;
	const truncated = displayName.length > 16 ? `${displayName.slice(0, 16)}…` : displayName;

	return (
		<div ref={wrapRef} style={{ position: "relative" }}>
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				style={identityChipStyle}
				aria-haspopup="menu"
				aria-expanded={open}
				title={displayName}
			>
				<Avatar user={user} size={20} />
				{!narrow && <span style={identityChipNameStyle}>{truncated}</span>}
				<span aria-hidden style={identityChipCaretStyle}>
					▾
				</span>
			</button>
			{open && (
				<div role="menu" style={identityMenuStyle}>
					<button
						type="button"
						role="menuitem"
						onClick={() => {
							setOpen(false);
							onSignOut();
						}}
						style={identityMenuItemStyle}
					>
						Sign out
					</button>
				</div>
			)}
		</div>
	);
}

const identityTextStyle: CSSProperties = {
	fontSize: 13,
	color: "white",
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis",
	maxWidth: 200,
};

const identityChipStyle: CSSProperties = {
	display: "inline-flex",
	alignItems: "center",
	gap: 6,
	background: "rgba(255,255,255,0.08)",
	border: "1px solid rgba(255,255,255,0.16)",
	color: "white",
	padding: "4px 8px 4px 4px",
	borderRadius: 999,
	cursor: "pointer",
	fontSize: 12,
	fontWeight: 500,
	fontFamily: "inherit",
};

const identityChipNameStyle: CSSProperties = {
	maxWidth: "16ch",
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
};

const identityChipCaretStyle: CSSProperties = {
	opacity: 0.7,
	fontSize: 10,
};

const identityMenuStyle: CSSProperties = {
	position: "absolute",
	right: 0,
	bottom: "calc(100% + 8px)",
	background: "white",
	color: "#111",
	border: "1px solid #e5e7eb",
	borderRadius: 8,
	boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
	minWidth: 140,
	padding: 4,
	zIndex: 2147483647,
};

const identityMenuItemStyle: CSSProperties = {
	display: "block",
	width: "100%",
	textAlign: "left",
	background: "transparent",
	border: "none",
	padding: "6px 10px",
	fontSize: 13,
	color: "#111",
	cursor: "pointer",
	borderRadius: 6,
};

const sidebarStyle: CSSProperties = {
	position: "fixed",
	top: 0,
	right: 0,
	bottom: 0,
	width: 360,
	maxWidth: "92vw",
	background: "white",
	borderLeft: "1px solid #e5e7eb",
	boxShadow: "-12px 0 32px rgba(0,0,0,0.12)",
	zIndex: 2147483646,
	display: "flex",
	flexDirection: "column",
	fontFamily:
		'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
	color: "#111",
};

const sidebarHeaderStyle: CSSProperties = {
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	padding: "12px 16px",
	borderBottom: "1px solid #e5e7eb",
};

const sidebarScrollStyle: CSSProperties = {
	flex: 1,
	overflow: "auto",
	padding: "12px 12px 24px",
};

const sidebarEmptyStyle: CSSProperties = {
	color: "#6b7280",
	fontSize: 13,
	padding: "24px 8px",
	textAlign: "center",
};

const sidebarPathStyle: CSSProperties = {
	fontSize: 11,
	textTransform: "uppercase",
	letterSpacing: 0.5,
	color: "#6b7280",
	padding: "4px 4px 6px",
	wordBreak: "break-all",
};

const sidebarItemStyle: CSSProperties = {
	display: "flex",
	alignItems: "flex-start",
	gap: 8,
	width: "100%",
	textAlign: "left",
	background: "white",
	border: "1px solid #e5e7eb",
	borderRadius: 6,
	padding: "8px 10px",
	cursor: "pointer",
	fontSize: 13,
	marginBottom: 6,
	color: "#111",
};

const sidebarItemIndexStyle: CSSProperties = {
	flexShrink: 0,
	width: 22,
	height: 22,
	borderRadius: 999,
	background: "#10b981",
	color: "white",
	fontWeight: 700,
	fontSize: 11,
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
};

const sidebarItemTextStyle: CSSProperties = {
	flex: 1,
	whiteSpace: "pre-wrap",
	wordBreak: "break-word",
};

const sidebarItemDoneStyle: CSSProperties = {
	flexShrink: 0,
	background: "#d1fae5",
	color: "#065f46",
	fontSize: 11,
	fontWeight: 600,
	padding: "2px 6px",
	borderRadius: 4,
	alignSelf: "center",
};

const submittedPanelStyle: CSSProperties = {
	position: "fixed",
	bottom: 16,
	left: "50%",
	transform: "translateX(-50%)",
	zIndex: 2147483646,
	display: "flex",
	flexDirection: "column",
	gap: 8,
	padding: "12px 16px",
	width: "min(420px, calc(100vw - 32px))",
	background: "white",
	border: "1px solid #e5e7eb",
	borderRadius: 12,
	boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
	fontFamily:
		'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
	color: "#111",
	pointerEvents: "auto",
};

const submittedHeaderStyle: CSSProperties = {
	display: "flex",
	alignItems: "center",
	gap: 8,
};

const submittedBadgeStyle: CSSProperties = {
	background: "#d1fae5",
	color: "#065f46",
	fontSize: 11,
	fontWeight: 700,
	padding: "2px 8px",
	borderRadius: 999,
	textTransform: "uppercase",
	letterSpacing: 0.5,
};

const submittedProgressLabelStyle: CSSProperties = {
	display: "flex",
	justifyContent: "space-between",
	fontSize: 12,
};

const submittedProgressTrackStyle: CSSProperties = {
	width: "100%",
	height: 6,
	background: "#f3f4f6",
	borderRadius: 999,
	overflow: "hidden",
};

const submittedProgressFillStyle: CSSProperties = {
	height: "100%",
	background: "#10b981",
	borderRadius: 999,
	transition: "width 500ms",
};

const submittedEtaStyle: CSSProperties = {
	margin: 0,
	fontSize: 12,
	color: "#6b7280",
};

export function mountFeedbackToolbar(): { unmount: () => void } | null {
	console.log("[YNS Feedback Toolbar] mountFeedbackToolbar() called", {
		isWindow: typeof window !== "undefined",
		vercelEnv: process.env.NEXT_PUBLIC_VERCEL_ENV,
		alreadyMounted: typeof document !== "undefined" && Boolean(document.getElementById(MOUNT_NODE_ID)),
	});
	if (typeof window === "undefined") return null;
	if (process.env.NEXT_PUBLIC_VERCEL_ENV !== "preview") {
		console.log(
			"[YNS Feedback Toolbar] gate failed — NEXT_PUBLIC_VERCEL_ENV is",
			process.env.NEXT_PUBLIC_VERCEL_ENV,
		);
		return null;
	}
	if (document.getElementById(MOUNT_NODE_ID)) return null;

	const container = document.createElement("div");
	container.id = MOUNT_NODE_ID;
	container.dataset.ynsFeedbackUi = "true";
	document.body.appendChild(container);

	const root = createRoot(container);
	root.render(<FeedbackToolbar />);

	return {
		unmount: () => {
			root.unmount();
			container.remove();
		},
	};
}

// Auto-mount on import. Wait for DOM ready.
if (typeof window !== "undefined") {
	console.log("[YNS Feedback Toolbar] module evaluated", {
		vercelEnv: process.env.NEXT_PUBLIC_VERCEL_ENV,
		readyState: document.readyState,
		willAutoMount: process.env.NEXT_PUBLIC_VERCEL_ENV === "preview",
	});
}
if (typeof window !== "undefined" && process.env.NEXT_PUBLIC_VERCEL_ENV === "preview") {
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", () => {
			mountFeedbackToolbar();
		});
	} else {
		mountFeedbackToolbar();
	}
}
